import { api } from '@/config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BotTriggerType = 'ad' | 'lead_ad_form' | 'keyword' | 'manual' | 'scheduled';

export interface ScheduledTriggerConfig {
  frequency: 'hourly' | 'daily' | 'weekly' | 'monthly';
  atHour?: number;
  atMinute?: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timezone?: string;
  audienceContactUids?: string[];
  audienceGroupUids?: string[];
  nextRunAt?: string;
  lastRunAt?: string;
}

export interface BotTriggerConfig {
  adIds?: string[];
  adSetIds?: string[];
  campaignIds?: string[];
  formIds?: string[];
  keywords?: string[];
  scheduled?: ScheduledTriggerConfig;
}

export interface BotStateBase {
  id: string;
  type: string;
}

export interface MessageState extends BotStateBase {
  type: 'message';
  prompt?: string;
  templateUid?: string;
  next?: string;
}

export interface ChoiceOption {
  label: string;
  value: string;
  nextStateId: string;
}

export interface ChoiceSection {
  title?: string;
  rows: ChoiceOption[];
}

export type ChoicePresentation = 'buttons' | 'list' | 'media_buttons';
export type ChoiceMediaType = 'image' | 'video' | 'document';

export interface ChoiceState extends BotStateBase {
  type: 'choice';
  prompt: string;
  /**
   * Flat options. For `presentationType === 'list'` this should still be
   * populated as a flattened mirror of `sections[].rows` so legacy code paths
   * (and inbound text-fallback matching) keep working.
   */
  options: ChoiceOption[];
  saveAs?: string;
  retryPrompt?: string;

  presentationType?: ChoicePresentation;
  mediaType?: ChoiceMediaType;
  mediaUrl?: string;
  mediaFilename?: string;
  headerText?: string;
  footerText?: string;
  /** CTA label rendered on the "open menu" button for list mode. */
  buttonText?: string;
  /** Section grouping — required when presentationType === 'list'. */
  sections?: ChoiceSection[];
}

export interface CollectValidation {
  regex?: string;
  min?: number;
  max?: number;
}

export interface CollectState extends BotStateBase {
  type: 'collect';
  prompt: string;
  expectedType: 'text' | 'phone' | 'email' | 'number' | 'image';
  validation?: CollectValidation;
  retryPrompt?: string;
  saveAs: string;
  next: string;
}

export interface ConditionBranch {
  when: string;
  nextStateId: string;
}

export interface ConditionState extends BotStateBase {
  type: 'condition';
  branches: ConditionBranch[];
  fallbackStateId: string;
}

export interface HandoffState extends BotStateBase {
  type: 'handoff';
  assignTo: 'unassigned' | 'round_robin' | string;
  notifyMessage?: string;
}

export interface EndState extends BotStateBase {
  type: 'end';
  terminalStatus?: 'qualified' | 'lost' | 'spam';
}

export interface AiState extends BotStateBase {
  type: 'ai';
  systemPrompt: string;
  endIntents: string[];
  intentToNextState: Record<string, string>;
  fallbackStateId?: string;
  maxTurnsBeforeHandoff?: number;
  saveTranscriptAs?: string;
}

export type BotState =
  | MessageState
  | ChoiceState
  | CollectState
  | ConditionState
  | HandoffState
  | EndState
  | AiState;

export interface BotFlow {
  _id: string;
  flowUid: string;
  companyUid: string;
  /** WhatsApp account this flow is bound to. Empty = legacy "any account" flow. */
  accountUid?: string;
  name: string;
  description?: string;
  triggerType: BotTriggerType;
  triggerConfig: BotTriggerConfig;
  states: BotState[];
  startStateId: string;
  isActive: boolean;
  /** IETF language tag (default 'en'). */
  language?: string;
  /** Cluster id linking translations of the same flow. */
  flowGroupUid?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBotFlowPayload {
  name: string;
  description?: string;
  /** Bind to a specific WhatsApp account; omit for legacy "any account". */
  accountUid?: string;
  triggerType: BotTriggerType;
  triggerConfig?: BotTriggerConfig;
  states: BotState[];
  startStateId: string;
  isActive?: boolean;
}

export interface UpdateBotFlowPayload {
  name?: string;
  description?: string;
  /** Reassign to another WhatsApp account (or empty string to clear). */
  accountUid?: string;
  triggerType?: BotTriggerType;
  triggerConfig?: BotTriggerConfig;
  states?: BotState[];
  startStateId?: string;
  isActive?: boolean;
  language?: string;
  flowGroupUid?: string;
}

export interface SimHistoryEntry {
  role: 'inbound' | 'outbound' | 'system';
  body: string;
}

export interface SimulatePayload {
  profileName?: string;
  phone?: string;
  history: SimHistoryEntry[];
  inbound: string;
}

export interface SimulateResult {
  outbound: { body: string; type: 'text' | 'list' | 'button' }[];
  nextState: string | null;
  status: string;
  fieldsAfter: Record<string, string>;
  terminal: boolean;
}

// ─── AI generation types ──────────────────────────────────────────────────────

export interface AiGenerateFlowParams {
  brief?: string;
  language?: string;
  tone?: string;
  ai_provider?: string;
  ai_model?: string;
}

export interface AiGeneratedFlowDraft {
  name: string;
  description: string;
  triggerType: string;
  triggerConfig: Record<string, unknown>;
  states: BotState[];
  startStateId: string;
  isActive: false;
}

export interface AiGenerateFlowResult {
  flow: AiGeneratedFlowDraft;
  reasoning: string;
  creditsCharged?: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const botFlowsService = {
  list: async (filter?: { q?: string; status?: 'active' | 'inactive'; accountUid?: string }): Promise<BotFlow[]> =>
    (await api.get('/slighter_crm/bot-flows', {
      params: {
        q: filter?.q?.trim() || undefined,
        status: filter?.status || undefined,
        accountUid: filter?.accountUid || undefined,
      },
    })).data,

  get: async (flowUid: string): Promise<BotFlow> =>
    (await api.get(`/slighter_crm/bot-flows/${flowUid}`)).data,

  create: async (payload: CreateBotFlowPayload): Promise<BotFlow> =>
    (await api.post('/slighter_crm/bot-flows', payload)).data,

  update: async (flowUid: string, payload: UpdateBotFlowPayload): Promise<BotFlow> =>
    (await api.patch(`/slighter_crm/bot-flows/${flowUid}`, payload)).data,

  activate: async (flowUid: string): Promise<BotFlow> =>
    (await api.post(`/slighter_crm/bot-flows/${flowUid}/activate`)).data,

  deactivate: async (flowUid: string): Promise<BotFlow> =>
    (await api.post(`/slighter_crm/bot-flows/${flowUid}/deactivate`)).data,

  remove: async (flowUid: string): Promise<{ ok: boolean }> =>
    (await api.delete(`/slighter_crm/bot-flows/${flowUid}`)).data,

  simulate: async (flowUid: string, payload: SimulatePayload): Promise<SimulateResult> =>
    (await api.post(`/slighter_crm/bot-flows/${flowUid}/simulate`, payload)).data,

  aiGenerate: async (params: AiGenerateFlowParams): Promise<AiGenerateFlowResult> =>
    (await api.post('/slighter_crm/bot-flows/ai-generate', params)).data,

  saveDraft: async (flow: AiGeneratedFlowDraft & { name: string }): Promise<BotFlow> =>
    (await api.post('/slighter_crm/bot-flows/save-draft', flow)).data,

  snapshots: async (flowUid: string): Promise<FlowSnapshot[]> =>
    (await api.get(`/slighter_crm/bot-flows/${flowUid}/snapshots`)).data,

  analytics: async (flowUid: string, range?: { from?: string; to?: string }): Promise<FlowAnalytics> =>
    (await api.get(`/slighter_crm/bot-flows/${flowUid}/analytics`, {
      params: { from: range?.from, to: range?.to },
    })).data,

  getWebhook: async (flowUid: string): Promise<{ secret: string; flowUid: string }> =>
    (await api.get(`/slighter_crm/bot-flows/${flowUid}/webhook`)).data,

  rotateWebhook: async (flowUid: string): Promise<{ secret: string; flowUid: string }> =>
    (await api.post(`/slighter_crm/bot-flows/${flowUid}/webhook/rotate`)).data,
};

export interface FlowSnapshot {
  snapshotUid: string;
  flowUid: string;
  version: number;
  name: string;
  description?: string;
  triggerType: string;
  states: unknown[];
  startStateId: string;
  createdBy?: string;
  createdAt: string;
}

export interface FlowAnalyticsStateRow {
  stateId: string;
  visits: number;
  advanced: number;
  terminated: number;
  inFlight: number;
}

export interface FlowAnalytics {
  flowUid: string;
  range: { from: string; to: string };
  totals: {
    entered: number;
    completed: number;
    inFlight: number;
    handedOff: number;
    errored: number;
    completionRate: number | null;
  };
  perState: FlowAnalyticsStateRow[];
}
